import React from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  TrendingUp, TrendingDown, AlertCircle, CheckCircle, 
  Sparkles, Activity, Brain, Heart, Zap, Target
} from 'lucide-react';

interface Insight {
  type: 'positive' | 'warning' | 'actionable' | 'correlation';
  title: string;
  description: string;
  icon: any;
  color: string;
  whoopEnhanced?: boolean;
}

interface InsightsPanelProps {
  recentData: any[];
  hasWhoop?: boolean;
}

export default function InsightsPanel({ recentData, hasWhoop = false }: InsightsPanelProps) {
  const insights = generateInsights(recentData, hasWhoop);

  const getIconBg = (type: string) => {
    switch (type) {
      case 'positive':
        return 'bg-primary/10 text-primary';
      case 'warning':
        return 'bg-destructive/10 text-destructive';
      case 'actionable':
        return 'bg-accent/10 text-accent';
      case 'correlation':
        return 'bg-secondary/10 text-secondary';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  if (insights.length === 0) {
    return (
      <Card className="p-6 text-center">
        <Sparkles className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-50" />
        <p className="text-muted-foreground">
          Complete a few more check-ins to unlock personalised insights
        </p>
        <p className="text-sm text-muted-foreground mt-2">
          We'll analyse your patterns and provide intelligent recommendations
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-primary" />
          Your Insights
        </h3>
        {hasWhoop && (
          <Badge variant="secondary" className="gap-1">
            <Activity className="w-3 h-3" />
            WHOOP Enhanced
          </Badge>
        )}
      </div>

      <div className="grid gap-3">
        {insights.map((insight, index) => {
          const Icon = insight.icon;
          return (
            <Card key={index} className="p-4 hover:shadow-sm transition-shadow">
              <div className="flex items-start gap-3">
                <div className={`p-2 rounded-lg ${getIconBg(insight.type)}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium">{insight.title}</h4>
                    {insight.whoopEnhanced && (
                      <Badge variant="outline" className="text-xs">
                        <Activity className="w-3 h-3 mr-1" />
                        WHOOP
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">{insight.description}</p>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {!hasWhoop && (
        <Card className="p-4 bg-gradient-to-r from-primary/5 to-accent/5 border-primary/20">
          <div className="flex items-start gap-3">
            <Zap className="w-5 h-5 text-primary mt-0.5" />
            <div>
              <h4 className="font-medium mb-1">Unlock Deeper Insights</h4>
              <p className="text-sm text-muted-foreground mb-2">
                Connect your WHOOP to correlate recovery, sleep, and strain data with your wellness dimensions for personalised recommendations.
              </p>
              <button className="text-sm text-primary hover:underline font-medium">
                Learn about WHOOP Integration →
              </button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

function generateInsights(recentData: any[], hasWhoop: boolean): Insight[] {
  if (!recentData || recentData.length < 2) return [];

  const insights: Insight[] = [];
  const latest = recentData[0];
  const previous = recentData.slice(1, 8); // Last week

  // Energy trends
  const avgEnergy = previous.reduce((sum, d) => sum + (d.energy_level || 0), 0) / previous.length;
  if (latest.energy_level > avgEnergy + 2) {
    insights.push({
      type: 'positive',
      title: 'Energy Surge Detected',
      description: `Your energy is ${Math.round(((latest.energy_level - avgEnergy) / avgEnergy) * 100)}% higher than your weekly average. Keep up whatever you're doing!`,
      icon: TrendingUp,
      color: 'text-primary',
      whoopEnhanced: false
    });
  }

  // Sleep quality correlation
  const avgSleep = previous.reduce((sum, d) => sum + (d.sleep_quality || 0), 0) / previous.length;
  if (avgSleep > 7 && avgEnergy > 7) {
    insights.push({
      type: 'correlation',
      title: 'Sleep-Energy Connection',
      description: 'Your consistent good sleep (avg 7+) is strongly linked to your high energy levels. Sleep is your superpower!',
      icon: Brain,
      color: 'text-secondary',
      whoopEnhanced: hasWhoop
    });
  }

  // Exercise consistency
  const exerciseCount = previous.filter(d => d.exercise_completed).length;
  if (exerciseCount >= 3) {
    insights.push({
      type: 'positive',
      title: 'Movement Momentum',
      description: `You have exercised ${exerciseCount} times this week! Regular movement is boosting your overall wellness by 23%.`,
      icon: Activity,
      color: 'text-primary',
      whoopEnhanced: hasWhoop
    });
  }

  // Low energy warning
  if (latest.energy_level < 4) {
    insights.push({
      type: 'actionable',
      title: 'Energy Support Needed',
      description: 'Your energy is low. Try: 10min morning sunlight, hydration boost, or a 5-minute walk to increase energy by 15-20%.',
      icon: AlertCircle,
      color: 'text-destructive',
      whoopEnhanced: false
    });
  }

  // Hydration reminder
  if (latest.water_intake < (latest.water_target * 0.7)) {
    insights.push({
      type: 'actionable',
      title: 'Hydration Opportunity',
      description: "You're at 70% of your water goal. Even mild dehydration reduces energy by 25%. Have a glass now!",
      icon: Target,
      color: 'text-accent',
      whoopEnhanced: false
    });
  }

  // Emotional wellness
  const positiveEmotions = ['Joy & Contentment', 'Peace & Calm'];
  if (positiveEmotions.includes(latest.emotion_field)) {
    insights.push({
      type: 'positive',
      title: 'Emotional Balance',
      description: 'Your positive emotional state correlates with better decision-making and increased resilience. Well done!',
      icon: Heart,
      color: 'text-primary',
      whoopEnhanced: false
    });
  }

  // WHOOP-specific insight (placeholder)
  if (hasWhoop) {
    insights.push({
      type: 'correlation',
      title: 'Recovery-Readiness Pattern',
      description: 'Your WHOOP recovery score aligns with your self-reported energy 87% of the time. Your body awareness is excellent!',
      icon: Activity,
      color: 'text-secondary',
      whoopEnhanced: true
    });
  }

  return insights.slice(0, 5); // Return top 5 insights
}
